import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const A4_WIDTH = 210; // mm
const A4_HEIGHT = 297; // mm
const MARGIN = 20; // mm
const CONTENT_WIDTH = A4_WIDTH - (MARGIN * 2);
const CONTENT_HEIGHT = A4_HEIGHT - (MARGIN * 2) - 30; // 30mm for footer

// Logo cache
let logoCache = null;
let logoLoadPromise = null;

// Load logo once and cache it
const loadLogo = async () => {
  if (logoCache) return logoCache;
  if (logoLoadPromise) return logoLoadPromise;

  logoLoadPromise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    // Try both possible locations
    const logoPaths = [
      '/swishx-updated-orng-logo.svg',
      process.env.PUBLIC_URL + '/swishx-updated-orng-logo.svg',
      `${window.location.origin}/swishx-updated-orng-logo.svg`
    ];
    
    let currentPathIndex = 0;
    
    const tryLoad = (path) => {
      img.onload = () => {
        try {
          // Convert SVG/Image to base64 with high quality
          const canvas = document.createElement('canvas');
          // Set reasonable dimensions for SVG (higher resolution for better quality)
          const scale = 2; // 2x scale for logo
          const width = (img.naturalWidth || img.width || 600) * scale;
          const height = (img.naturalHeight || img.height || 140) * scale;
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Enable better image rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw image to canvas at higher resolution
          ctx.drawImage(img, 0, 0, width, height);
          const base64 = canvas.toDataURL('image/png', 1.0); // Maximum quality
          logoCache = base64;
          resolve(base64);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to convert logo to base64:', error);
          }
          resolve(null);
        }
      };
      
      img.onerror = () => {
        if (currentPathIndex < logoPaths.length - 1) {
          currentPathIndex++;
          img.src = logoPaths[currentPathIndex];
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn('SwishX logo not found at any path, continuing without logo');
          }
          resolve(null);
        }
      };
      
      img.src = path;
    };
    
    tryLoad(logoPaths[0]);
  });

  return logoLoadPromise;
};

// Convert pixels to mm (assuming 96 DPI)
const pxToMm = (px, dpi = 96) => (px * 25.4) / dpi;

// Convert mm to pixels
const mmToPx = (mm, dpi = 96) => (mm * dpi) / 25.4;

export const generatePDF = async ({
  title,
  subtitle,
  sections,
  pageMeta,
  dateRange,
  options = {}
}) => {
  const {
    includeCoverPage = true,
    includeFilterPage = true,
    includeRawData = false,
    quality = 'standard',
    selectedSections = null, // null means all
    onProgress = null // Progress callback
  } = options;

  // Load logo once at start
  const logoData = await loadLogo();

  const doc = new jsPDF('portrait', 'mm', 'a4');
  let pageNumber = 1;
  const totalPages = sections.length + (includeCoverPage ? 1 : 0) + (includeFilterPage ? 1 : 0);

  // Helper to add logo watermark to page (top-right, subtle)
  const addLogoWatermark = (doc) => {
    if (!logoData) return;
    try {
      // Top-right watermark: ~60px width
      const logoWidthMm = 15; // ~60px converted to mm
      const logoHeightMm = 3.5; // Maintain aspect ratio (140/600 * 15)
      const x = A4_WIDTH - MARGIN - logoWidthMm;
      const y = MARGIN;
      
      // Add logo as watermark (jsPDF will render it, opacity handled by image itself)
      doc.addImage(logoData, 'PNG', x, y, logoWidthMm, logoHeightMm, undefined, 'FAST');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to add logo watermark:', error);
      }
    }
  };

  // Cover Page
  if (includeCoverPage) {
    // Add logo at top-left (cover page)
    if (logoData) {
      try {
        const logoWidthMm = 30; // ~120px converted to mm
        const logoHeightMm = 7; // Maintain aspect ratio (140/600 * 30)
        const logoX = MARGIN;
        const logoY = MARGIN;
        doc.addImage(logoData, 'PNG', logoX, logoY, logoWidthMm, logoHeightMm, undefined, 'FAST');
        
        // Add text below logo
        const textY = logoY + logoHeightMm + 6;
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('SwishX sQMS', logoX, textY);
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('Schedule-M & WHO-GMP Compliance Platform', logoX, textY + 5);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to add logo to cover page:', error);
        }
      }
    }
    
    doc.setFillColor(253, 72, 22); // #fd4816
    doc.rect(0, 0, A4_WIDTH, 60, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text('SwishX sQMS', MARGIN, 30);
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'normal');
    doc.text('Schedule-M & WHO-GMP aligned | Always audit-ready', MARGIN, 45);
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(title, MARGIN, 80);
    
    if (subtitle) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text(subtitle, MARGIN, 95);
    }
    
    doc.setFontSize(12);
    doc.text(`Plant: ${pageMeta.plant}`, MARGIN, 120);
    doc.text(`Area: ${pageMeta.area}`, MARGIN, 130);
    doc.text(`Date Range: ${dateRange.label || dateRange.start + ' to ' + dateRange.end}`, MARGIN, 140);
    doc.text(`Role: ${pageMeta.role}`, MARGIN, 150);
    doc.text(`Generated: ${pageMeta.timestamp}`, MARGIN, 160);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('This report is generated from the sQMS demo environment (front-end prototype).', MARGIN, 250);
    
    addFooter(doc, pageNumber, totalPages);
    doc.addPage();
    pageNumber++;
  }

  // Filters & Context Page
  if (includeFilterPage) {
    addLogoWatermark(doc);
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Filters & Context', MARGIN, 30);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    let yPos = 50;
    doc.text(`Plant: ${pageMeta.plant}`, MARGIN, yPos);
    yPos += 10;
    doc.text(`Area: ${pageMeta.area}`, MARGIN, yPos);
    yPos += 10;
    doc.text(`Date Range: ${dateRange.label || dateRange.start + ' to ' + dateRange.end}`, MARGIN, yPos);
    yPos += 10;
    doc.text(`Role: ${pageMeta.role}`, MARGIN, yPos);
    yPos += 10;
    doc.text(`Time Scope: ${pageMeta.timeRange}`, MARGIN, yPos);
    yPos += 20;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Exported Sections:', MARGIN, yPos);
    yPos += 10;
    doc.setFont('helvetica', 'normal');
    
    const sectionsToExport = selectedSections || sections.map(s => s.id);
    sections
      .filter(s => sectionsToExport.includes(s.id))
      .forEach((section, idx) => {
        doc.text(`${idx + 1}. ${section.title}`, MARGIN + 5, yPos);
        if (section.description) {
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(section.description, MARGIN + 10, yPos + 5);
          doc.setFontSize(11);
          doc.setTextColor(0, 0, 0);
          yPos += 8;
        } else {
          yPos += 7;
        }
      });
    
    addFooter(doc, pageNumber, totalPages);
    doc.addPage();
    pageNumber++;
  }

  // Export sections
  const sectionsToExport = selectedSections 
    ? sections.filter(s => selectedSections.includes(s.id))
    : sections;

  for (let i = 0; i < sectionsToExport.length; i++) {
    const section = sectionsToExport[i];
    const element = section.elementRef?.current;
    
    if (!element) continue;

    // Update progress
    if (onProgress) {
      onProgress(i + 1, sectionsToExport.length);
    }

    try {
      // Wait for fonts and images to load
      await new Promise(resolve => {
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => {
            // Additional wait for images and SVGs
            setTimeout(resolve, 400);
          });
        } else {
          setTimeout(resolve, 500);
        }
      });

      // Ensure all images in the element are loaded
      const images = element.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = resolve; // Continue even if image fails
            setTimeout(resolve, 2000); // Timeout after 2s
          });
        })
      );

      // Ensure element is visible and rendered
      const originalStyle = {
        visibility: element.style.visibility,
        display: element.style.display,
        opacity: element.style.opacity,
        position: element.style.position,
        transform: element.style.transform
      };
      
      // Make sure element is visible for capture
      element.style.visibility = 'visible';
      element.style.display = 'block';
      element.style.opacity = '1';
      element.style.position = 'relative';
      element.style.transform = 'none';

      // Scroll element into view if needed
      element.scrollIntoView({ behavior: 'instant', block: 'start' });
      
      // Wait for any animations/transitions and rendering
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 200);
          });
        });
      });

      // Calculate optimal scale based on quality
      const baseScale = quality === 'high' ? 3 : 2; // Increased from 2/1 to 3/2
      const maxCanvasSize = 16384; // Browser canvas size limit
      const elementWidth = element.scrollWidth || element.offsetWidth || element.clientWidth;
      const elementHeight = element.scrollHeight || element.offsetHeight || element.clientHeight;
      
      // Calculate scale that won't exceed canvas limits
      const calculatedScale = Math.min(
        baseScale,
        Math.floor(maxCanvasSize / Math.max(elementWidth, elementHeight))
      );
      
      // Capture section as image with improved settings
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: Math.max(calculatedScale, 2), // Minimum 2x scale
        useCORS: true,
        logging: false,
        allowTaint: false,
        foreignObjectRendering: true, // Better SVG support
        removeContainer: false,
        imageTimeout: 20000, // Wait longer for images (20s)
        proxy: undefined, // No proxy needed
        onclone: (clonedDoc) => {
          // Ensure all fonts are loaded in cloned document
          const clonedElement = clonedDoc.querySelector(`[data-export-section-id="${section.id}"]`);
          if (clonedElement) {
            // Force font rendering and styles
            const computedStyle = window.getComputedStyle(element);
            clonedElement.style.fontFamily = computedStyle.fontFamily;
            clonedElement.style.fontSize = computedStyle.fontSize;
            clonedElement.style.color = computedStyle.color;
            clonedElement.style.backgroundColor = computedStyle.backgroundColor || '#ffffff';
            
            // Ensure all SVG elements are visible and properly rendered
            const svgs = clonedElement.querySelectorAll('svg');
            svgs.forEach(svg => {
              svg.style.visibility = 'visible';
              svg.style.display = 'block';
              svg.style.opacity = '1';
              // Ensure SVG fills are preserved
              const paths = svg.querySelectorAll('path, circle, rect, polygon');
              paths.forEach(path => {
                const fill = window.getComputedStyle(path).fill;
                if (fill && fill !== 'none') {
                  path.setAttribute('fill', fill);
                }
              });
            });
            
            // Ensure all images have proper styling and are loaded
            const imgs = clonedElement.querySelectorAll('img');
            imgs.forEach(img => {
              img.style.maxWidth = '100%';
              img.style.height = 'auto';
              img.style.display = 'block';
              // Force image to load if not already loaded
              if (!img.complete) {
                img.src = img.src; // Trigger reload
              }
            });
            
            // Fix any broken text rendering
            const textElements = clonedElement.querySelectorAll('*');
            textElements.forEach(el => {
              const style = window.getComputedStyle(el);
              if (style.fontFamily) {
                el.style.fontFamily = style.fontFamily;
              }
            });
          }
        },
        width: elementWidth,
        height: elementHeight,
        windowWidth: elementWidth,
        windowHeight: elementHeight,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0
      });

      // Restore original styles
      element.style.visibility = originalStyle.visibility;
      element.style.display = originalStyle.display;
      element.style.opacity = originalStyle.opacity;

      // Use higher quality image encoding
      const imgData = canvas.toDataURL('image/png', 1.0); // Maximum quality
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate dimensions to fit page width (use actual DPI from scale)
      const actualDpi = calculatedScale * 96;
      const ratio = CONTENT_WIDTH / pxToMm(imgWidth, actualDpi);
      const imgWidthMm = CONTENT_WIDTH;
      const imgHeightMm = pxToMm(imgHeight, actualDpi) * ratio;
      
      // Add logo watermark
      addLogoWatermark(doc);
      
      // Add section title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(section.title, MARGIN, 25);
      
      if (section.description) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        doc.text(section.description, MARGIN, 32);
      }
      
      // Split image across pages if needed
      // For better quality, we'll create proper slices of the canvas
      let remainingHeight = imgHeightMm;
      let sourceYpx = 0;
      let pageY = 40; // Start below title
      
      while (remainingHeight > 0) {
        const pageHeight = CONTENT_HEIGHT - pageY;
        const heightToAdd = Math.min(remainingHeight, pageHeight);
        const sourceHeightPx = Math.ceil((heightToAdd / imgHeightMm) * imgHeight);
        
        // Create a slice canvas for this page to ensure clean rendering
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = imgWidth;
        sliceCanvas.height = sourceHeightPx;
        const sliceCtx = sliceCanvas.getContext('2d');
        
        // Enable high-quality rendering
        sliceCtx.imageSmoothingEnabled = true;
        sliceCtx.imageSmoothingQuality = 'high';
        
        // Draw the slice from the original canvas
        const tempImg = new Image();
        await new Promise((resolve) => {
          tempImg.onload = () => {
            sliceCtx.drawImage(
              tempImg,
              0, sourceYpx, imgWidth, sourceHeightPx,
              0, 0, imgWidth, sourceHeightPx
            );
            const sliceData = sliceCanvas.toDataURL('image/png', 1.0);
            
            // Add the slice to PDF with best quality
            doc.addImage(
              sliceData,
              'PNG',
              MARGIN,
              pageY,
              imgWidthMm,
              heightToAdd,
              undefined,
              'SLOW' // Best quality rendering
            );
            resolve();
          };
          tempImg.onerror = () => {
            // Fallback: use original method
            doc.addImage(
              imgData,
              'PNG',
              MARGIN,
              pageY,
              imgWidthMm,
              heightToAdd,
              undefined,
              'SLOW'
            );
            resolve();
          };
          tempImg.src = imgData;
        });
        
        remainingHeight -= heightToAdd;
        sourceYpx += sourceHeightPx;
        pageY = MARGIN;
        
        if (remainingHeight > 0) {
          addFooter(doc, pageNumber, totalPages);
          doc.addPage();
          pageNumber++;
          addLogoWatermark(doc);
        }
      }
      
      // Add footer and new page (unless last section)
      if (i < sectionsToExport.length - 1) {
        addFooter(doc, pageNumber, totalPages);
        doc.addPage();
        pageNumber++;
      }
    } catch (error) {
      console.error(`Error capturing section ${section.title}:`, error);
      // Add error message to PDF
      doc.setFontSize(12);
      doc.setTextColor(200, 0, 0);
      doc.text(`Error capturing section: ${section.title}`, MARGIN, 40);
    }
  }

  // Add footer to last page
  addFooter(doc, pageNumber, totalPages);

  return doc;
};

const addFooter = (doc, pageNumber, totalPages) => {
  const footerY = A4_HEIGHT - 10;
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  const footerText = `SwishX sQMS | Schedule-M / WHO-GMP | Generated ${new Date().toLocaleDateString('en-IN')} | Page ${pageNumber} of ${totalPages}`;
  doc.text(footerText, MARGIN, footerY, { align: 'left' });
  
  // Add "Generated by SwishX sQMS" text
  doc.setFontSize(7);
  doc.text('Generated by SwishX sQMS', A4_WIDTH - MARGIN, footerY, { align: 'right' });
};

export const downloadPDF = async (config, filename) => {
  const doc = await generatePDF(config);
  doc.save(filename || `sQMS_${config.title}_${new Date().toISOString().split('T')[0]}.pdf`);
};
